import {
  ColumnActivePanel,
  ColumnAddedPanel,
  SidePanel,
} from 'components/core';
import { findLastIndex, isEqual, kebabCase } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { ColumnContext } from 'utils/context';
import {
  IDisplayOptions,
  TActiveColumn,
} from 'utils/context/ViewContext/types';

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Flex,
  Icon,
  IconButton,
} from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';

import { sortConfig, toggleGroupConfig } from './utils';

type Panel = 'TOGGLE_COLUMNS' | 'REF_KEYS';

const PANEL_TITLE: Record<Panel, string> = {
  TOGGLE_COLUMNS: 'core.column',
  REF_KEYS: 'core.addReferenceKey',
};

interface IColumnPanel {
  isOrder?: boolean;
  onClose: () => void;
  updateColumns: (displayOptions: IDisplayOptions) => void;
}

const getKeys = (columns: TActiveColumn[]) =>
  columns.filter(({ id }) => /ref-/i.test(id)).map(({ label }) => label);

function ColumnPanel({
  isOrder = false,
  onClose,
  updateColumns,
}: IColumnPanel) {
  const { t } = useTranslation();
  const { viewColumns } = useContext(ColumnContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refKeys, setRefKeys] = useState(getKeys(viewColumns));
  const [visiblePanel, setVisiblePanel] = useState<Panel>('TOGGLE_COLUMNS');

  useEffect(() => {
    const added = getKeys(viewColumns);
    setRefKeys(added);
  }, [viewColumns]);

  const triggerWarning = (callback: () => void) => {
    if (!isEqual(refKeys, getKeys(viewColumns))) {
      setIsModalOpen(true);
    } else {
      callback();
    }
  };

  const handleReturn = () => {
    setIsModalOpen(false);
    setRefKeys(getKeys(viewColumns));
    setVisiblePanel('TOGGLE_COLUMNS');
  };

  const handleClose = () => {
    triggerWarning(() => {
      handleReturn();
      onClose();
    });
  };

  const handleGoBack = () => {
    triggerWarning(handleReturn);
  };

  const handleSelectKey = (refKey: string) => (isSelected: boolean) => {
    setRefKeys((prev) => {
      if (isSelected) return [...prev, refKey];
      return prev.filter((sel) => sel !== refKey);
    });
  };

  const handleAddedColumns = (added: string[]) => {
    const refIds = added.map((label) => `ref-${kebabCase(label)}`);
    const colIds = viewColumns.map(({ id }) => id);
    const next = viewColumns.filter(
      ({ id }) => !/ref-/i.test(id) || refIds.includes(id),
    );
    added.forEach((label) => {
      const id = `ref-${kebabCase(label)}`;
      if (!colIds.includes(id)) next.push({ id, label, enabled: true });
    });
    updateColumns({ activeColumns: next });
  };

  const handleToggleColumns = (columnId: string) => {
    const next = [...viewColumns];
    const colIndex = next.findIndex(({ id }) => id === columnId);
    const lastEnabled = findLastIndex(next, ({ enabled }) => enabled);
    const insertIndex = next[colIndex].enabled ? lastEnabled : lastEnabled + 1;

    const isGroupedElement = (columnId: string) => {
      return Object.values(toggleGroupConfig).some((config) =>
        config.some(({ id }) => id === columnId),
      );
    };

    const findGroupKey = (id: string): string | null => {
      const groupKey = Object.keys(toggleGroupConfig).find((key) =>
        toggleGroupConfig[key].some((item) => item.id === id),
      );
      return groupKey || null;
    };

    const areAllIdsDisabled = (ids: string[]) => {
      return ids.every((id) => {
        const element = next.find((item) => item.id === id);
        return element && element.enabled === false;
      });
    };

    const column = isGroupedElement(columnId);
    const key = findGroupKey(columnId);

    if (column && key) {
      const ids = toggleGroupConfig[key]
        .filter(({ id }) => id !== columnId)
        .map(({ id }) => id);
      if (areAllIdsDisabled(ids)) {
        next.splice(insertIndex, 0, next.splice(colIndex, 1)[0]);
      }
    } else {
      next.splice(insertIndex, 0, next.splice(colIndex, 1)[0]);
    }

    if (!isOrder) {
      for (const [key, value] of Object.entries(sortConfig)) {
        for (let index = 0; index < value.length; index++) {
          const current = next.findIndex(({ id }) => id === value[index]);
          const target = next.findIndex(({ id }) => id === key);
          const insertIndex =
            target > current ? target + index : target + index + 1;
          next.splice(insertIndex, 0, next.splice(current, 1)[0]);
        }
      }
    }

    updateColumns({
      activeColumns: next.map((col) => ({
        ...col,
        enabled: col.id === columnId ? !col.enabled : col.enabled,
      })),
    });
  };

  const handleToggleGroup = (columnIds: string[], currentState: boolean) => {
    const next = [...viewColumns];
    const lastEnabledIdx = findLastIndex(next, ({ enabled }) => enabled);

    for (let index = 0; index < columnIds.length; index++) {
      const current = next.findIndex(({ id }) => id === columnIds[index]);
      const target = lastEnabledIdx;
      const insertIndex = current > target ? target + index + 1 : target;
      next.splice(insertIndex, 0, next.splice(current, 1)[0]);
    }

    const columns = next.map((col) => {
      const enabledState = columnIds.includes(col.id)
        ? !currentState
        : col.enabled;
      const disabledState =
        col.id == columnIds[0] ? !currentState : col.enabled;
      const state = currentState ? enabledState : disabledState;
      return {
        ...col,
        enabled: state,
      };
    });

    updateColumns({
      activeColumns: columns,
    });
  };

  const handleSort = (sortIds: string[]) => {
    const nextIds = [
      isOrder ? 'order-number' : 'shipment-id',
      ...sortIds
        .map((item) => toggleGroupConfig[item]?.map(({ id }) => id) || item)
        .flat(),
    ];

    const next = [...viewColumns];
    const active = next.filter(({ id }) => nextIds.includes(id));
    const activeList = nextIds.map((id) => active.find((col) => col.id === id));

    const disabled = next.filter(({ enabled }) => !enabled).map(({ id }) => id);
    const disabledList = next.filter(({ id }) => disabled.includes(id));
    const filteredDisabledList = disabledList.filter(
      ({ id }) => !nextIds.includes(id),
    );

    const nextColumns = activeList.concat(filteredDisabledList).filter(Boolean);
    updateColumns({ activeColumns: nextColumns as TActiveColumn[] });
  };

  return (
    <SidePanel
      title={t(PANEL_TITLE[visiblePanel])}
      onClose={visiblePanel === 'REF_KEYS' ? handleClose : onClose}
      startIcon={
        visiblePanel === 'REF_KEYS' ? (
          <IconButton
            aria-label={t('core.columnSelectBack')}
            onPress={handleGoBack}
            variant="tertiary"
            children={<Icon icon="chevron_left" />}
          />
        ) : undefined
      }
    >
      <Flex orientation="vertical" css={{ flexGrow: 1, overflowY: 'auto' }}>
        {visiblePanel === 'TOGGLE_COLUMNS' ? (
          <ColumnActivePanel
            handleToggle={handleToggleColumns}
            handleToggleGroup={handleToggleGroup}
            isOrder={isOrder}
            onClose={onClose}
            onPress={!isOrder ? () => setVisiblePanel('REF_KEYS') : undefined}
            onSort={handleSort}
            visibleOptions={viewColumns}
            toggleOptions={viewColumns
              .filter(({ enabled }) => !enabled)
              .map(({ id }) => id)}
          />
        ) : (
          <ColumnAddedPanel
            addedColumns={refKeys}
            onSelectKey={handleSelectKey}
            onCancel={handleReturn}
            onConfirm={() => {
              handleAddedColumns(refKeys);
              setVisiblePanel('TOGGLE_COLUMNS');
            }}
          />
        )}
      </Flex>
      <Dialog isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <DialogHeader>{t('core.unsavedChangesWarningTitle')}</DialogHeader>
        <DialogContent>
          {t('core.unsavedChangesWarningDescription')}
        </DialogContent>
        <DialogFooter>
          <Button variant="secondary" onPress={() => setIsModalOpen(false)}>
            {t('global.cancel')}
          </Button>
          <Button onPress={handleReturn} variant="danger">
            {t('global.confirm')}
          </Button>
        </DialogFooter>
      </Dialog>
    </SidePanel>
  );
}

export default ColumnPanel;
